import React, { useEffect } from "react";
import useScreenRotation from "./useScreenRotation";
import MapSlide from "./MapSlide";
import {
  ScreenLogoBar, ScreenSkyline, FallbackBackground, LoadingSlide, EmptySlide,
  ProgressDot, formatPubDate, CalendarDays,
} from "./ScreenParts";

function useScreenChrome() {
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.head.removeChild(tag);
      document.body.style.overflow = prev;
    };
  }, []);
}

export default function ScreenNewsHorizontal() {
  useScreenChrome();
  return <HorizontalShell />;
}

function HorizontalShell() {
  const { current, index, total, news, loading } = useScreenRotation();

  return (
    <div className="fixed inset-0 bg-[#F8FAF5] text-[hsl(var(--brand-ink))] overflow-hidden flex flex-col" data-testid="screen-horizontal">
      {/* Header */}
      <header className="flex items-center justify-between px-10 pt-6 pb-3 shrink-0">
        <ScreenLogoBar size="md" />
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Live-Newsticker</p>
          <p className="text-sm text-neutral-700 mt-0.5">{total > 0 ? `${index + 1} / ${total}` : ""}</p>
        </div>
      </header>

      {/* Stage — flex-1 so it takes remaining space ABOVE the skyline */}
      <main className="flex-1 relative overflow-hidden min-h-0">
        {loading && <LoadingSlide orientation="horizontal" />}
        {!loading && current === null && news.length === 0 && <EmptySlide orientation="horizontal" />}
        {current && current.type === "news" && <NewsSlideHorizontal item={current.payload} key={`n-${index}`} />}
        {current && current.type === "map" && <MapSlide sperrungen={current.sperrungen} dataTestId="map-slide-horizontal" key={`m-${index}`} />}
      </main>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 py-3 shrink-0">
        {total > 0 && Array.from({ length: Math.min(total, 12) }).map((_, i) => {
          const offset = total > 12 ? Math.floor(index / 12) * 12 : 0;
          return <ProgressDot key={i} active={(i + offset) === index} />;
        })}
      </div>

      {/* Skyline — natural height, always full */}
      <ScreenSkyline />
    </div>
  );
}

function NewsSlideHorizontal({ item }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center px-10 py-6 screen-fade-in">
      <div className="w-full max-w-[1600px] grid grid-cols-12 gap-12 items-center">
        {/* Image (left, ~55%) */}
        <div className="col-span-7 relative overflow-hidden rounded-sm shadow-[0_24px_60px_-30px_rgba(0,0,0,0.30)] bg-neutral-100 aspect-[16/10]">
          {item.image_url ? (
            <img src={item.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <FallbackBackground />
          )}
        </div>

        {/* Text panel (right, ~45%) */}
        <div className="col-span-5 flex flex-col justify-center min-w-0">
          <p className="text-[12px] uppercase tracking-[0.2em] text-[hsl(var(--brand-green-strong))] mb-3 font-medium">
            Aktuelles aus Detmold
          </p>
          {item.pub_date && (
            <p className="text-sm text-neutral-500 inline-flex items-center gap-2 mb-5">
              <CalendarDays className="h-3.5 w-3.5" /> {formatPubDate(item.pub_date)}
            </p>
          )}
          <h2 className="font-semibold leading-[1.05] mb-6 tracking-tight" style={{ fontSize: "clamp(2rem, 3.4vw, 4rem)" }}>
            {item.title}
          </h2>
          {item.description && (
            <p className="text-neutral-700 leading-relaxed line-clamp-6" style={{ fontSize: "clamp(1rem, 1.25vw, 1.4rem)" }}>
              {item.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

